import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "./icons";
import { Card, CardHeader, cx } from "./ui";
import { CopyField } from "./CopyField";
import { RegionTag } from "./RegionTag";
import { useEgress } from "../lib/queries";

/**
 * The addresses this organization's workloads call out from.
 *
 * Shown where somebody types a connection string, because the failure it
 * prevents happens there: an app pointed at a database inside the customer's
 * own VPC connects to nothing, and the reason — their firewall does not know
 * this platform — is invisible from every screen in this dashboard.
 *
 * Three honesty properties, all of which cost a support ticket if dropped. An
 * unknown address says so rather than rendering an empty field, because a blank
 * copied into a firewall rule is a rule that matches nothing. Each address is
 * labelled with the region it belongs to, since an organization in two regions
 * calls out from two places and allowing one through is half a fix. And the
 * caveat is stated on the card rather than in a doc: these can change when the
 * platform moves a workload between machines, and a customer who allowlisted
 * the old one loses connectivity with nothing here reporting a fault.
 */
export function EgressCard({ orgId }: { orgId: string | undefined }) {
  const { t } = useTranslation("app");
  const { data, isPending } = useEgress(orgId);

  // Nothing placed yet — no workload exists to call out from, so a card here
  // would be an address somebody waits for rather than uses.
  if (isPending || !data || data.data.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title={t("egress.title")}
        description={t("egress.lead")}
      />
      <div className="space-y-4 p-5">
        {data.data.map((entry) => (
          <div key={entry.region ?? "unregioned"} className="space-y-2">
            <div className="flex items-center gap-2">
              {entry.region ? (
                <RegionTag region={entry.region} />
              ) : (
                <span className="text-xs text-fg-subtle">{t("egress.unregioned")}</span>
              )}
            </div>
            {entry.address ? (
              <CopyField
                value={entry.address}
                label={t("egress.address", {
                  region: entry.region ?? t("egress.yourWorkloads"),
                })}
              />
            ) : (
              <p className="text-sm text-fg-subtle">{t("egress.unknown")}</p>
            )}
          </div>
        ))}

        {!data.stable && (
          <p className="text-xs text-fg-subtle">
            {t("egress.note")}
          </p>
        )}
      </div>

      {/*
        The other half of the question, at the moment it is being asked.
        Allowlisting only works on a database that has a public address; one
        that publishes nothing needs a tunnel, and nobody browses to a nav item
        for a feature they have not heard of. This is where they are already
        standing — pasting a connection string that will not connect.
      */}
      <Link
        to="/tunnels"
        className={cx(
          "flex items-center justify-between gap-3 border-t border-line px-5 py-3",
          "text-sm text-fg-muted transition-colors duration-200 ease-smooth hover:text-fg",
        )}
      >
        <span>{t("egress.tunnel")}</span>
        <Icon name="chevron" className="size-3.5 shrink-0 text-fg-subtle" />
      </Link>
    </Card>
  );
}
